import type { ReactElement } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Tooltip, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { BooleanField, Datagrid, TextField, useListContext, useRecordContext } from "react-admin";
import { useNavigate } from "react-router";
import { grants } from "../../backoffice.access_control";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Row } from "../../misc/Row";
import type { DrivingSchoolOtherEventCategory } from "../../providers/drivingSchoolOtherEventCategoriesProvider";
import { OtherEventCategoryMobileListItem } from "./OtherEventCategoryMobileListItem";

function OtherEventCategoryNameField(props: { source: string; label: string }) {
  const { source } = props;
  const category = useRecordContext<DrivingSchoolOtherEventCategory>();

  if (!category) {
    return null;
  }

  return (
    <Row gap={0.5}>
      <TextField source={source} />
      {category.isSystemCategory && (
        <Tooltip title="Nicht bearbeitbar">
          <LockOutlinedIcon fontSize="small" color="action" aria-label="Nicht bearbeitbar" />
        </Tooltip>
      )}
    </Row>
  );
}

export function OtherEventCategoriesDataGrid(props: { empty?: ReactElement }) {
  const { empty } = props;
  const { isLoading, data, total } = useListContext<DrivingSchoolOtherEventCategory>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const canEdit = grants.has("OtherEvent:category:edit");

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((category) => (
          <OtherEventCategoryMobileListItem
            key={category.id}
            category={category}
            onClick={
              canEdit && !category.isSystemCategory ? () => navigate(`/otherEventCategories/${category.id}`) : undefined
            }
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid
      bulkActionButtons={false}
      empty={empty}
      rowClick={(_id, _resource, record) => (canEdit && !record.isSystemCategory ? "edit" : false)}
    >
      <OtherEventCategoryNameField source="name" label="Name" />
      <TextField source="description" label="Beschreibung" />
      <BooleanField source="isPaid" label="Bezahlt" />
    </Datagrid>
  );
}
